import { ChangeDetectorRef, Directive, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { SocialFeedService } from '../social-feed/services/social-feed.service';
import {
  PaginatedResponse,
  SocialAuthor,
  SocialGameGroup,
  SocialGroup,
  SocialPost,
} from '../social-feed/models/social-feed.model';
import { ComposerTarget } from '../social-feed/components/social-composer/social-composer.component';

type GroupTab = 'posts' | 'members' | 'about';

/**
 * A map group or a game group page (they differ only in where the data comes
 * from): header, posts (pinned first), members, about. Both pages share this
 * class and social-group.component.html.
 */
@Directive()
export abstract class GroupPageBase implements OnInit {
  abstract readonly kind: 'map' | 'game';
  protected abstract fetchGroup(id: number): Observable<SocialGroup | SocialGameGroup>;
  protected abstract fetchPosts(id: number, page: number): Observable<PaginatedResponse<SocialPost>>;
  protected abstract fetchMembers(id: number): Observable<SocialAuthor[]>;
  protected abstract routeParam: string;

  id = 0;
  group: SocialGroup | SocialGameGroup | null = null;
  loading = true;
  notFound = false;
  tab: GroupTab = 'posts';

  posts: SocialPost[] = [];
  postsLoading = false;
  postsError = false;
  page = 1;
  hasMore = false;
  loadingMore = false;
  highlightId: number | null = null;

  members: SocialAuthor[] = [];
  membersLoading = false;

  constructor(
    protected social: SocialFeedService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.highlightId = Number(this.route.snapshot.queryParamMap.get('post')) || null;
    this.id = Number(this.route.snapshot.paramMap.get(this.routeParam));
    this.fetchGroup(this.id).subscribe({
      next: (group) => {
        this.group = group;
        this.loading = false;
        this.loadPosts();
        this.cdr.markForCheck();
      },
      error: () => {
        // Restricted or missing: never say which.
        this.notFound = true;
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  get composerTarget(): ComposerTarget {
    return this.kind === 'map' ? { group: this.id } : { gameGroup: this.id };
  }

  get subtitle(): string | null {
    const g = this.group as any;
    return (this.kind === 'map' ? g?.map_name : g?.game_name) || null;
  }

  get mapId(): string | null {
    return (this.group as any)?.map_id || null;
  }

  selectTab(tab: GroupTab): void {
    this.tab = tab;
    if (tab === 'members' && !this.members.length) this.loadMembers();
  }

  loadPosts(): void {
    this.postsLoading = true;
    this.postsError = false;
    this.fetchPosts(this.id, 1).subscribe({
      next: (page) => {
        this.posts = page.results;
        this.page = 1;
        this.hasMore = !!page.next;
        this.postsLoading = false;
        this.cdr.markForCheck();
        this.scrollToHighlight();
      },
      error: () => {
        this.posts = [];
        this.postsLoading = false;
        this.postsError = true;
        this.cdr.markForCheck();
      },
    });
  }

  loadMore(): void {
    if (this.loadingMore || !this.hasMore) return;
    this.loadingMore = true;
    const next = this.page + 1;
    this.fetchPosts(this.id, next).subscribe({
      next: (page) => {
        const have = new Set(this.posts.map((p) => p.id));
        this.posts = [...this.posts, ...page.results.filter((p) => !have.has(p.id))];
        this.page = next;
        this.hasMore = !!page.next;
        this.loadingMore = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadingMore = false;
        this.cdr.markForCheck();
      },
    });
  }

  loadMembers(): void {
    this.membersLoading = true;
    this.fetchMembers(this.id).subscribe({
      next: (members) => {
        this.members = members;
        this.membersLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.membersLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  onRemoved(id: number): void {
    this.posts = this.posts.filter((p) => p.id !== id);
  }

  openMap(): void {
    if (this.mapId) this.router.navigate(['/map', this.mapId]);
  }

  openProfile(id: number): void {
    this.router.navigate(['/social-profile', id]);
  }

  imageUrl(path: string | null | undefined): string {
    return this.social.resolveImageUrl(path);
  }

  initials(name: string | undefined | null): string {
    return (name || '').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  }

  private scrollToHighlight(): void {
    const id = this.highlightId;
    if (!id) return;
    setTimeout(() => {
      document.getElementById('post-' + id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        this.highlightId = null;
        this.cdr.markForCheck();
      }, 2500);
    }, 50);
  }
}
